import type { ComponentType, ReactNode } from 'react';
import React from 'react';
import type { IconProps } from '@noths/polaris-client-ribbons-design-system';
import {
  IconCalendar,
  IconShipping,
  IconShippingFree,
} from '@noths/polaris-client-ribbons-design-system';
import type { Price } from '@noths/polaris-dev-ts-types';

import { IconCircle } from 'src/components/atoms/IconCircle/IconCircle';
import type { DecoratedDeliveryOption } from 'src/redux/delivery/types';
import { NO_DELIVERY_OPTIONS_AVAILABLE } from '../../constants/copy';
import { getCutoffMessage } from '../../utils/getCutoffMessage';
import { getDeliveryOptionHeading } from '../../utils/getDeliveryOptionHeading';
import { getDeliveryOptionMessage } from '../../utils/getDeliveryOptionMessage';
import { getFormattedDeliveryDate } from '../../utils/getFormattedDeliveryDate';
import { getFormattedPrice } from '../../utils/getFormattedPrice';
import * as styles from './DeliverySummary.styles';

export interface ItemProps {
  Icon: ComponentType<IconProps>;
  children?: ReactNode;
  htmlMessage?: string;
}

const Item = ({ Icon, children, htmlMessage }: ItemProps) => {
  return (
    <li css={styles.item}>
      <IconCircle Icon={Icon} />
      <p
        css={styles.message}
        {...(htmlMessage ? { dangerouslySetInnerHTML: { __html: htmlMessage } } : {})}
      >
        {!htmlMessage ? children : null}
      </p>
    </li>
  );
};

interface DeliverySummaryProps {
  cheapestOption?: DecoratedDeliveryOption;
  numberOfOptions: number;
  partnerFreeDeliveryThreshold: Price | null;
  partnerName: string;
}

export const DeliverySummary = ({
  cheapestOption,
  numberOfOptions,
  partnerFreeDeliveryThreshold,
  partnerName,
}: DeliverySummaryProps) => {
  if (!cheapestOption) {
    return <p css={styles.noOptionMessage}>{NO_DELIVERY_OPTIONS_AVAILABLE}</p>;
  }

  const {
    cutoffDateTime,
    date,
    exactDate,
    max,
    min,
    optionCode,
    price: { amount, currencyCode },
    providerName,
  } = cheapestOption;
  const cutoffMessage = getCutoffMessage(cutoffDateTime);
  const htmlMessage = getDeliveryOptionMessage({
    cutoffMessage: cutoffMessage
      ? `<time dateTime="${cutoffDateTime}">${cutoffMessage}</time>`
      : undefined,
    dateMessage:
      date && exactDate
        ? `<b><time dateTime="${date}">${getFormattedDeliveryDate(date)}</time></b>`
        : undefined,
    optionCode,
    providerName,
    range: min && max ? `${min}-${max}` : undefined,
  });
  const pluraliseOption = numberOfOptions - 1 > 1 ? 'options' : 'option';
  const showFreeDeliveryThreshold =
    partnerFreeDeliveryThreshold?.currency === currencyCode && optionCode !== 'FREE';

  return (
    <ul css={styles.list}>
      <Item Icon={IconCalendar} htmlMessage={htmlMessage} />
      <Item Icon={IconShipping}>
        <b>{getFormattedPrice(Number(amount), currencyCode)}</b>{' '}
        {getDeliveryOptionHeading(optionCode).toLowerCase()} delivery{' '}
        {numberOfOptions > 1 ? `and ${numberOfOptions - 1} more ${pluraliseOption}` : ''}
      </Item>
      {showFreeDeliveryThreshold && (
        <Item Icon={IconShippingFree}>
          <b>Free</b> standard delivery on orders over{' '}
          {getFormattedPrice(partnerFreeDeliveryThreshold.amount / 100, currencyCode)} from{' '}
          {partnerName}
        </Item>
      )}
    </ul>
  );
};
